import React, { useState } from 'react';
import { FiSave, FiCheck, FiDownload, FiClock, FiX, FiRotateCcw } from 'react-icons/fi';
import { saveAiCache, getAiCacheHistory, type AiCacheContentType, type AiCacheHistoryEntry } from '../../api/ai';

interface Props {
  architectureId?: string;
  cacheType?: AiCacheContentType;
  content: any;
  fromCache: boolean;
  onRestore?: (entry: AiCacheHistoryEntry) => void;
  onExportPdf?: () => void;
  updatedAt?: string;
}

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const AiCacheActions: React.FC<Props> = ({ architectureId, cacheType, content, fromCache, onRestore, onExportPdf, updatedAt }) => {
  const [saved, setSaved]           = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory]       = useState<AiCacheHistoryEntry[]>([]);
  const [histLoading, setHistLoading] = useState(false);

  if (!architectureId || !cacheType) return null;

  const handleSave = async () => {
    try {
      await saveAiCache(architectureId, cacheType, content);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch {}
  };

  const handleHistory = async () => {
    if (showHistory) { setShowHistory(false); return; }
    setHistLoading(true);
    try {
      const res = await getAiCacheHistory(architectureId, cacheType);
      setHistory(res.history);
    } catch {
      setHistory([]);
    } finally {
      setHistLoading(false);
      setShowHistory(true);
    }
  };

  return (
    <div className="relative flex items-center gap-1.5">
      {/* Save */}
      <button
        onClick={handleSave}
        title="Sauvegarder"
        className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
          saved
            ? 'border-green-300 bg-green-50 text-green-700'
            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
        }`}
      >
        {saved ? <FiCheck size={12} className="text-green-600" /> : <FiSave size={12} />}
        {saved ? 'Sauvegardé' : 'Sauvegarder'}
      </button>

      {/* Export PDF */}
      {onExportPdf && (
        <button
          onClick={onExportPdf}
          title="Exporter en PDF"
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
        >
          <FiDownload size={12} /> PDF
        </button>
      )}

      {/* Historique */}
      <div className="relative">
        <button
          onClick={handleHistory}
          title="Historique des versions"
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-colors ${
            showHistory ? 'border-indigo-300 bg-indigo-50 text-indigo-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          <FiClock size={12} /> Historique
        </button>

        {showHistory && (
          <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
              <span className="text-xs font-semibold text-gray-700">Versions précédentes</span>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={13} />
              </button>
            </div>
            {histLoading ? (
              <div className="p-4 text-xs text-gray-400 text-center">Chargement…</div>
            ) : history.length === 0 ? (
              <div className="p-4 text-xs text-gray-400 text-center">Aucune version précédente</div>
            ) : (
              <ul className="max-h-56 overflow-y-auto divide-y divide-gray-50">
                {history.map((entry, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-2 hover:bg-gray-50">
                    <div>
                      <p className="text-xs font-medium text-gray-700">Version {history.length - i}</p>
                      <p className="text-xs text-gray-400">{formatDate(entry.savedAt)}</p>
                      {entry.metadata?.tokensUsed && (
                        <p className="text-xs text-gray-400">{entry.metadata.tokensUsed.toLocaleString()} tokens</p>
                      )}
                    </div>
                    {onRestore && (
                      <button
                        onClick={() => { onRestore(entry); setShowHistory(false); }}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 px-2 py-1 rounded-lg hover:bg-indigo-50"
                      >
                        <FiRotateCcw size={11} /> Restaurer
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Indicateur cache */}
      {fromCache && updatedAt && (
        <span className="text-xs text-blue-500 font-medium ml-1">
          Depuis le cache · {formatDate(updatedAt)}
        </span>
      )}
    </div>
  );
};

export default AiCacheActions;
